import * as Sentry from "@sentry/react";
import { EditorContent, useEditor } from "@tiptap/react";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import usePrevious from "../../../hooks/usePrevious";
import Auth from "src/Auth";
import { db } from "../../../Firebase";
import { getAuthUser } from "../../../lib/effectiveAuthUser";
import { toggleSnapshotFavorite } from "../../../services/documentFavorites";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import HistoryModal from "../../HistoryModal/HistoryModal";
import { getBigDocumentSnapshotContent } from "../documentSnapshots";
import { createHistoryPreviewExtensions, getFirstNonEmptyText } from "./historyContent";
import { HistoryPreviewContent } from "./styles";

const SUPPORTED_HISTORY_MARK_TYPES = new Set(["annotation", "bold", "deletion", "highlight", "inserted", "insertion", "italic", "link", "strike", "underline"]);

const sanitizeHistorySnapshotNode = (node) => {
  if (!node || typeof node !== "object") {
    return node;
  }

  if (Array.isArray(node)) {
    let changed = false;
    const sanitizedItems = node.map((item) => {
      const sanitizedItem = sanitizeHistorySnapshotNode(item);
      if (sanitizedItem !== item) {
        changed = true;
      }
      return sanitizedItem;
    });
    return changed ? sanitizedItems : node;
  }

  let sanitizedMarks = node.marks;
  if (Array.isArray(node.marks)) {
    const supportedMarks = node.marks.filter((mark) => SUPPORTED_HISTORY_MARK_TYPES.has(mark?.type));
    if (supportedMarks.length !== node.marks.length) {
      sanitizedMarks = supportedMarks;
    }
  }

  let sanitizedContent = node.content;
  if (Array.isArray(node.content)) {
    let changed = false;
    const supportedContent = node.content.map((item) => {
      const sanitizedItem = sanitizeHistorySnapshotNode(item);
      if (sanitizedItem !== item) {
        changed = true;
      }
      return sanitizedItem;
    });
    if (changed) {
      sanitizedContent = supportedContent;
    }
  }

  if (sanitizedMarks !== node.marks || sanitizedContent !== node.content) {
    const sanitizedNode = { ...node };
    if (sanitizedMarks !== node.marks) {
      sanitizedNode.marks = sanitizedMarks;
    }
    if (sanitizedContent !== node.content) {
      sanitizedNode.content = sanitizedContent;
    }
    return sanitizedNode;
  }

  return node;
};

const History = ({ onRestore, close, documentId, isOpen, editorComputedStyles, isCreatingSnapshot }) => {
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [preloadedData, setPreloadedData] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const previousSnapshotListRef = useRef(null);
  const storyEngineIdRef = useStoryBibleStore((state) => state.storyEngineIdRef);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const user = getAuthUser();
  const [showError, setShowError] = useState(false);
  const prevIsCreatingSnapshot = usePrevious(isCreatingSnapshot);
  const componentMountedRef = useRef(true);
  const isSelectingSnapshotRef = useRef(false);

  // Component cleanup
  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Clear selected snapshot when switching documents to prevent ID mismatch
  useEffect(() => {
    setSelectedSnapshot(null);
  }, [documentId]);

  const editor = useEditor({
    editable: false,
    extensions: createHistoryPreviewExtensions(),
    onContentError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: "document-history-tiptap-editor-content-error",
          snapshotId: selectedSnapshot?.id,
          documentId,
          userId: user.uid,
        },
      });
    },
    enableContentCheck: true,
    content: "",
  });

  // Mirrors what applyEditorComputedStyles sets on #editor so the shared
  // typography rules (paragraph spacing var, indent-via-::before) resolve the
  // same way in the preview. text-indent itself is not set: the shared rules
  // zero it and draw the indent from the `indented` class instead.
  const style = {
    fontFamily: editorComputedStyles.fontFamily,
    fontSize: editorComputedStyles.fontSize,
    lineHeight: editorComputedStyles.lineHeight,
    "--sw-paragraph-spacing": editorComputedStyles.paragraphSpacing || "0px",
  };

  // Paginated fetch function for document snapshots
  const fetchHistoryPaginated = useCallback(
    async (limit = 50, offset = 0) => {
      if (!documentId || !user) return [];

      try {
        let query = db.collection("userData").doc(user.uid).collection("documentSnapshots").doc(documentId).collection("tiptapSnapshots").orderBy("createdAt", "desc");

        // For offset-based pagination, use startAfter with proper cursor management
        if (offset > 0) {
          // First, get the cursor document at the offset position
          const cursorQuery = query.limit(offset);
          const cursorDocs = await cursorQuery.get();

          if (cursorDocs.docs.length === offset) {
            // We have the exact cursor position, start after the last document
            const cursorDoc = cursorDocs.docs[offset - 1];
            query = query.startAfter(cursorDoc).limit(limit);
          } else {
            // Not enough documents to paginate further, but still try the query
            // This prevents returning empty results when pagination logic fails
            query = query.limit(limit);
          }
        } else {
          query = query.limit(limit);
        }

        const snapshot = await query.get();

        const normalizedDocs = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();

            const getContent = async (fetchBigSnapshot = false) => {
              let content = data.content;

              if (fetchBigSnapshot && data.contentUuid) {
                content = await getBigDocumentSnapshotContent(documentId, data.contentUuid).catch(() => data.content);
              }

              content ||= "{}";

              if (typeof content !== "string") {
                return content;
              }
              return JSON.parse(content);
            };

            let summary = data.summary;

            if (!summary) {
              const parsedContent = await getContent(false);
              summary = getFirstNonEmptyText(parsedContent.default);
            }

            summary = summary.substring(0, 20);

            // Pre-check if content is empty so we can filter synchronously later
            const parsedContent = await getContent(false);
            const isEmpty = isEmptyContent(parsedContent);

            return {
              id: doc.id,
              documentId,
              createdAt: data.createdAt.toDate().toISOString(),
              isFavorite: data.isFavorite || false,
              data: {
                displayName: summary,
                isFavorite: data.isFavorite || false,
              },
              getContent,
              summary,
              isEmpty,
            };
          })
        );

        return normalizedDocs;
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: "document-history-fetch-paginated",
            documentId,
            userId: user?.uid,
            limit,
            offset,
          },
        });
        throw error; // Re-throw so the pagination hook can handle the error state
      }
    },
    [documentId, user]
  );

  // Sync preloadedData with real-time snapshots from the onSnapshot listener
  useEffect(() => {
    if (snapshots.length > 0) {
      const formattedData = snapshots.map((snapshot) => ({
        id: snapshot.id,
        documentId: snapshot.documentId,
        createdAt: snapshot.createdAt.toISOString(),
        isFavorite: snapshot.isFavorite || false,
        data: {
          displayName: snapshot.summary,
          isFavorite: snapshot.isFavorite || false,
        },
        getContent: snapshot.getContent,
        summary: snapshot.summary,
        isEmpty: snapshot.isEmpty,
      }));
      setPreloadedData(formattedData);
    }
  }, [snapshots]);

  // Client-safe version of getProseMirrorPlainText (matches server-side logic)
  const getProseMirrorPlainText = (json) => {
    if (!json || !json.default || !json.default.content || json.default.content.length === 0) {
      return "";
    }

    const extractText = (nodes) => {
      let text = "";
      for (const node of nodes) {
        if (node.type === "text") {
          text += node.text || "";
        } else if (node.content && Array.isArray(node.content)) {
          text += extractText(node.content);
        }
        // Add newlines between block nodes for proper text spacing
        if (node.type === "paragraph" || node.type === "heading") {
          text += "\n";
        }
      }
      return text;
    };

    return extractText(json.default.content).trim();
  };

  // Helper function to check if snapshot content is empty using the same logic as server-side
  const isEmptyContent = (content) => {
    if (!content) {
      return true;
    }

    const text = getProseMirrorPlainText(content);
    return !text || text.trim().length === 0;
  };

  // Synchronous filtering using pre-computed isEmpty flag
  const checkIfItemIsEmptySync = (item) => {
    // Find the corresponding snapshot to check its pre-computed isEmpty flag
    const snap = snapshots.find((s) => s.id === item.id);
    if (!snap) return false; // If we can't find the snapshot, don't filter it out

    // Use the pre-computed isEmpty flag that was determined when we loaded the content
    return snap.isEmpty || false;
  };

  // Legacy fetch for backward compatibility
  useEffect(() => {
    if (!user || !documentId) return;

    let isMounted = true;

    const normalizeDoc = async (doc) => {
      const data = doc.data();

      const getContent = async (fetchBigSnapshot = false) => {
        let content = data.content;

        if (fetchBigSnapshot && data.contentUuid) {
          content = await getBigDocumentSnapshotContent(documentId, data.contentUuid).catch(() => data.content);
        }

        content ||= "{}";

        if (typeof content !== "string") {
          return content;
        }
        return JSON.parse(content);
      };

      let summary = data.summary;

      if (!summary) {
        const parsedContent = await getContent(false);
        summary = getFirstNonEmptyText(parsedContent.default);
      }

      summary = summary.substring(0, 20);

      // Pre-check if content is empty for consistent filtering
      const parsedContent = await getContent(false);
      const isEmpty = isEmptyContent(parsedContent);

      return {
        getContent,
        summary,
        createdAt: data.createdAt.toDate(),
        id: doc.id,
        documentId,
        isEmpty,
        isFavorite: data.isFavorite || false,
      };
    };

    let query = db.collection("userData").doc(user.uid).collection("documentSnapshots").doc(documentId).collection("tiptapSnapshots").orderBy("createdAt", "desc").limit(50);

    const unsubscribe = query.onSnapshot(async (snapshot) => {
      try {
        const normalizedDocs = await Promise.all(snapshot.docs.map((doc) => normalizeDoc(doc)));
        if (isMounted) {
          setSnapshots(normalizedDocs);
          if (normalizedDocs.length > 0) {
            setSelectedSnapshot(normalizedDocs[0]);
          }
        }
      } catch (error) {
        if (isMounted) {
          Sentry.captureException(error, {
            tags: {
              feature: "document-history-legacy-fetch",
              documentId,
              userId: user?.uid,
            },
          });
          setSnapshots([]);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [documentId, user]);

  // Effect to handle content loading for selected snapshots
  useEffect(() => {
    // Prevents the editor from updating when the selected snapshot is changed.
    let shouldUpdate = true;

    if (editor) {
      if (!selectedSnapshot) {
        editor.commands.setContent("");
      } else {
        setIsContentLoading(true);
        selectedSnapshot
          .getContent(true)
          .then((contents) => {
            if (shouldUpdate) {
              editor.commands.setContent(sanitizeHistorySnapshotNode(contents?.default || ""));
              setIsContentLoading(false);
              setShowError(false);
            }
          })
          .catch(async (error) => {
            if (shouldUpdate) {
              // set editor contents to empty and briefly show the error
              editor.commands.setContent("");
              setShowError(true);
              setIsContentLoading(false);
              // attempt to clean the mark type, if successful, the error will be cleared
              try {
                await handleMarkTypeError(selectedSnapshot);
              } catch (cleanupError) {
                Sentry.captureException(cleanupError, {
                  tags: {
                    feature: "document-history-clean-annotation-mark-type",
                    documentId,
                    snapshotId: selectedSnapshot.id,
                    userId: user.uid,
                  },
                  extra: {
                    originalErrorMessage: error?.message,
                  },
                });
              }
            }
          });
      }
    }

    return () => {
      shouldUpdate = false;
    };
  }, [selectedSnapshot]);

  const handleSnapshotSelect = async (historyItem) => {
    // Prevent infinite loops by checking if we're already selecting this snapshot
    if (isSelectingSnapshotRef.current || selectedSnapshot?.id === historyItem?.id) {
      return;
    }

    isSelectingSnapshotRef.current = true;

    if (fetchHistoryPaginated) {
      const snapshot = {
        id: historyItem.id,
        documentId: historyItem.documentId ?? documentId,
        createdAt: new Date(historyItem.createdAt),
        summary: historyItem.data?.displayName || "",
        isFavorite: historyItem.data?.isFavorite || false,
        getContent: async (fetchBigSnapshot = false) => {
          // Capture documentId at callback creation time to handle document switching
          const snapshotDocumentId = historyItem.documentId ?? documentId;
          try {
            // Fetch the full content for this specific snapshot
            const query = db.collection("userData").doc(user.uid).collection("documentSnapshots").doc(snapshotDocumentId).collection("tiptapSnapshots").doc(historyItem.id);
            const doc = await query.get();

            if (!doc.exists) {
              throw new Error("Snapshot not found");
            }

            const data = doc.data();
            let content = data.content;

            if (fetchBigSnapshot && data.contentUuid) {
              content = await getBigDocumentSnapshotContent(snapshotDocumentId, data.contentUuid).catch(() => data.content);
            }

            content ||= "{}";

            if (typeof content !== "string") {
              return content;
            }
            return JSON.parse(content);
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                feature: "document-history-paginated-content-fetch",
                documentId: snapshotDocumentId,
                userId: user?.uid,
                snapshotId: historyItem.id,
              },
            });
            throw error;
          }
        },
      };
      setSelectedSnapshot(snapshot);
    }

    // Reset the selection flag
    setTimeout(() => {
      isSelectingSnapshotRef.current = false;
    }, 100);
  };

  const handleRestore = () => {
    if (selectedSnapshot) {
      selectedSnapshot.getContent(true).then((contents) => {
        onRestore(contents.default);
        close();
      });
    }
  };

  const handleMarkTypeError = async (snapshot = selectedSnapshot) => {
    if (!snapshot) return;
    await Auth.fetchWithIdToken("/api/document/snapshots/clean-annotation-mark-type", {
      method: "POST",
      docId: snapshot.documentId,
      snapshotId: snapshot.id,
    });
    setShowError(false);
  };

  const handleToggleFavorite = async (snapshotId) => {
    if (!user || !documentId) return;
    setSavingFavorite(true);
    try {
      // Toggle favorite directly - the HistoryModal will handle refreshing the data
      const newIsFavorite = await toggleSnapshotFavorite(user.uid, documentId, snapshotId);
      // Return the new favorite status for optimistic updates
      return newIsFavorite;
    } catch (error) {
      if (componentMountedRef.current) {
        Sentry.captureException(error, {
          tags: {
            feature: "document-history-toggle-favorite",
            documentId,
            userId: user.uid,
            snapshotId,
          },
        });
        setShowError("Failed to toggle favorite status. Please try again.");
      }
      throw error; // Re-throw so the caller knows it failed
    } finally {
      if (componentMountedRef.current) {
        setSavingFavorite(false);
      }
    }
  };

  // Only render the modal when both documentId and user are available
  if (!documentId || !user) return null;

  return (
    <HistoryModal
      key={documentId}
      isOpen={isOpen}
      onClose={close}
      selectedItem={
        <div className="selected-item p-4 pt-8 text-[var(--grey-0)]">
          {selectedSnapshot && (
            <HistoryPreviewContent
              style={style}
              className={classNames({ indented: editorComputedStyles.useTextIndent })}
            >
              <EditorContent editor={editor} />
            </HistoryPreviewContent>
          )}
        </div>
      }
      historyItems={[]}
      onSelectItem={handleSnapshotSelect}
      onRestoreItem={handleRestore}
      title="Document History"
      selectedItemId={selectedSnapshot?.id}
      sourceTable="tiptapSnapshots"
      fetchHistory={fetchHistoryPaginated}
      usePagination={true}
      filterEmptyItems={checkIfItemIsEmptySync}
      loading={isContentLoading}
      restoring={false}
      onToggleFavorite={handleToggleFavorite}
      isCreatingSnapshot={isCreatingSnapshot}
      isSavingFavorite={savingFavorite}
      preloadedData={preloadedData}
    />
  );
};

export default History;
